import React, { useState, useEffect } from "react";
import { Campaign, CampaignScheduleConfig } from "../../services/api";
import { Modal } from "../Common/Modal";
import {
  HiClock,
  HiCalendar,
  HiGlobe,
  HiPhone,
  HiExclamation,
} from "react-icons/hi";

interface ScheduleConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  campaign: Campaign;
  onSave: (config: CampaignScheduleConfig) => Promise<void>;
}

const TIMEZONE_OPTIONS = [
  { value: "Asia/Kolkata", label: "India Standard Time (IST)" },
  { value: "America/New_York", label: "Eastern Time (ET)" },
  { value: "America/Chicago", label: "Central Time (CT)" },
  { value: "America/Denver", label: "Mountain Time (MT)" },
  { value: "America/Los_Angeles", label: "Pacific Time (PT)" },
  { value: "America/Phoenix", label: "Arizona Time" },
  { value: "Europe/London", label: "London (GMT)" },
  { value: "Europe/Paris", label: "Paris (CET)" },
  { value: "Asia/Tokyo", label: "Tokyo (JST)" },
  { value: "Asia/Shanghai", label: "Shanghai (CST)" },
  { value: "Australia/Sydney", label: "Sydney (AEDT)" },
];

const DAYS_OF_WEEK = [
  { value: "mon", label: "Monday" },
  { value: "tue", label: "Tuesday" },
  { value: "wed", label: "Wednesday" },
  { value: "thu", label: "Thursday" },
  { value: "fri", label: "Friday" },
  { value: "sat", label: "Saturday" },
  { value: "sun", label: "Sunday" },
];

const ScheduleConfigModal: React.FC<ScheduleConfigModalProps> = ({
  isOpen,
  onClose,
  campaign,
  onSave,
}) => {
  // Extract settings from campaign
  const campaignSettings = campaign.settings || {};
  const callingHours = campaignSettings.calling_hours || {};
  const callLimits = campaignSettings.call_limits || {};
  
  const [config, setConfig] = useState<CampaignScheduleConfig>({
    timezone: campaignSettings.timezone || "Asia/Calcutta",
    calling_hours: {
      start_time: callingHours.start_time || "09:00",
      end_time: callingHours.end_time || "17:00",
    },
    active_days: campaignSettings.active_days?.map(day => day.toLowerCase()) || ["mon", "tue", "wed", "thu", "fri"],
    call_limits: {
      max_per_day: callLimits.max_per_day || 1000,
      max_concurrent: callLimits.max_concurrent || 25,
    },
  });
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      const campaignSettings = campaign.settings || {};
      const callingHours = campaignSettings.calling_hours || {};
      const callLimits = campaignSettings.call_limits || {};
      
      setConfig({
        timezone: campaignSettings.timezone || "Asia/Calcutta",
        calling_hours: {
          start_time: callingHours.start_time || "09:00",
          end_time: callingHours.end_time || "17:00",
        },
        active_days: campaignSettings.active_days?.map(day => day.toLowerCase()) || ["mon", "tue", "wed", "thu", "fri"],
        call_limits: {
          max_per_day: callLimits.max_per_day || 1000,
          max_concurrent: callLimits.max_concurrent || 25,
        },
      });
      setError(null);
    }
  }, [isOpen, campaign]);

  const handleDayToggle = (day: string) => {
    const currentDays = config.active_days || [];
    if (currentDays.includes(day)) {
      setConfig({
        ...config,
        active_days: currentDays.filter(d => d !== day),
      });
    } else {
      setConfig({
        ...config,
        active_days: [...currentDays, day],
      });
    }
  };

  const handleSave = async () => {
    // Validation
    if (!config.calling_hours?.start_time || !config.calling_hours?.end_time) {
      setError("Please set calling hours");
      return;
    }

    if (config.calling_hours.start_time >= config.calling_hours.end_time) {
      setError("End time must be after start time");
      return;
    }

    if (!config.active_days || config.active_days.length === 0) {
      setError("Please select at least one active day");
      return;
    }

    if (config.calling_hours.start_time < "09:00" || config.calling_hours.end_time > "21:00") {
      setError("Warning: Calling hours should typically be between 9 AM and 9 PM for TCPA compliance");
    }

    setIsSaving(true);
    try {
      await onSave(config);
      onClose();
    } catch (err: any) {
      setError(err.message || "Failed to update schedule");
    } finally {
      setIsSaving(false);
    }
  };

  const estimatedCallsPerDay = () => {
    if (!config.calling_hours?.start_time || !config.calling_hours?.end_time) return 0;
    
    const start = parseInt(config.calling_hours.start_time.split(':')[0]);
    const end = parseInt(config.calling_hours.end_time.split(':')[0]);
    const hoursPerDay = end - start;
    const callsPerHour = 60; // Estimate
    
    return Math.min(hoursPerDay * callsPerHour, config.call_limits?.max_per_day || 1000);
  };

  const estimatedCompletionDays = () => {
    const callsPerDay = estimatedCallsPerDay();
    if (callsPerDay === 0) return 0;
    
    const activeDaysCount = config.active_days?.length || 0;
    if (activeDaysCount === 0) return 0;
    
    const totalContacts = campaign.total_contacts || 0;
    const remainingContacts = totalContacts - (campaign.processed_contacts || 0);
    
    const totalDays = Math.ceil(remainingContacts / callsPerDay);
    const weeks = Math.ceil(totalDays / activeDaysCount);
    
    return weeks * 7;
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Campaign Schedule Configuration"
    >
      <div className="mx-6 py-4 flex flex-col gap-4">
        {error && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center">
              <HiExclamation className="h-5 w-5 text-red-600 mr-2" />
              <p className="text-red-800">{error}</p>
            </div>
          </div>
        )}

        {/* Calling Hours */}
        <div>
          <label className="block text-sm font-medium text-[var(--foreground)] mb-2">
            <HiClock className="inline h-4 w-4 mr-1" />
            Calling Hours
          </label>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-[var(--foreground)] mb-1">Start Time</label>
              <input
                type="time"
                value={config.calling_hours?.start_time || ""}
                onChange={(e) => setConfig({ 
                  ...config, 
                  calling_hours: { 
                    start_time: e.target.value,
                    end_time: config.calling_hours?.end_time || "17:00"
                  } 
                })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent"
              />
            </div>
            <div>
              <label className="block text-xs text-[var(--foreground)] mb-1">End Time</label>
              <input
                type="time"
                value={config.calling_hours?.end_time || ""}
                onChange={(e) => setConfig({ 
                  ...config, 
                  calling_hours: { 
                    start_time: config.calling_hours?.start_time || "09:00",
                    end_time: e.target.value 
                  } 
                })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent"
              />
            </div>
          </div>
          <p className="text-xs text-[var(--foreground-muted)] mt-1">
            TCPA compliance: Recommended hours are 9 AM - 9 PM in recipient's timezone
          </p>
        </div>

        {/* Timezone */}
        <div>
          <label className="block text-sm font-medium text-[var(--foreground)] mb-2">
            <HiGlobe className="inline h-4 w-4 mr-1" />
            Campaign Timezone
          </label>
          <select
            value={config.timezone || ""}
            onChange={(e) => setConfig({ ...config, timezone: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent"
          >
            {TIMEZONE_OPTIONS.map(tz => (
              <option key={tz.value} value={tz.value}>{tz.label}</option>
            ))}
          </select>
        </div>

        {/* Active Days */}
        <div>
          <label className="block text-sm font-medium text-[var(--foreground)] mb-2">
            <HiCalendar className="inline h-4 w-4 mr-1" />
            Active Days
          </label>
          <div className="grid grid-cols-7 gap-2">
            {DAYS_OF_WEEK.map(day => (
              <button
                key={day.value}
                type="button"
                onClick={() => handleDayToggle(day.value)}
                className={`px-3 py-2 text-sm rounded-lg border transition-colors ${
                  config.active_days?.includes(day.value)
                    ? "bg-primary text-primary-foreground border-primary"
                    : "bg-[var(--card)] text-[var(--foreground-muted)] border-[var(--border)] hover:bg-[var(--muted)]"
                }`}
              >
                {day.label.slice(0, 3)}
              </button>
            ))}
          </div>
        </div>

        {/* Rate Limits */}
        <div>
          <label className="block text-sm font-medium text-[var(--foreground)] mb-2">
            <HiPhone className="inline h-4 w-4 mr-1" />
            Rate Limits
          </label>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-[var(--foreground)] mb-1">Max Calls/Day</label>
              <input
                type="number"
                min="1"
                max="5000"
                value={config.call_limits?.max_per_day || ""}
                onChange={(e) => setConfig({ 
                  ...config, 
                  call_limits: { 
                    max_per_day: parseInt(e.target.value) || 0,
                    max_concurrent: config.call_limits?.max_concurrent || 25
                  } 
                })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent"
              />
            </div>
            <div>
              <label className="block text-xs text-[var(--foreground)] mb-1">Max Concurrent</label>
              <input
                type="number"
                min="1"
                max="100"
                value={config.call_limits?.max_concurrent || ""}
                onChange={(e) => setConfig({ 
                  ...config, 
                  call_limits: { 
                    max_per_day: config.call_limits?.max_per_day || 1000,
                    max_concurrent: parseInt(e.target.value) || 0 
                  } 
                })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent"
              />
            </div>
          </div>
        </div>

        {/* Impact Preview */}
        <div className="bg-background rounded-lg p-4">
          <h4 className="text-sm font-medium text-[var(--foreground)] mb-2">Impact Preview</h4>
          <div className="space-y-1 text-sm text-[var(--foreground)]">
            <p>• Estimated calls per day: {estimatedCallsPerDay()}</p>
            <p>• Active days per week: {config.active_days?.length || 0}</p>
            <p>• Estimated completion: ~{estimatedCompletionDays()} days</p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end space-x-3 py-4 border-t">
          <button
            onClick={onClose}
            disabled={isSaving}
            className="px-4 py-2 text-[var(--foreground)] bg-[var(--card)] border border-[var(--border)] rounded-lg hover:bg-[var(--muted)] disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="px-4 py-2 text-primary-foreground bg-primary rounded-lg hover:bg-primary/90 disabled:opacity-50"
          >
            {isSaving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default ScheduleConfigModal;